import { useCallback, useEffect, useState } from "react";
import Button from "@components/ui/Button";
import Input from "@components/ui/Input";
import Textarea from "@components/ui/Textarea";
import Spinner from "@components/ui/Spinner";
import ErrorBanner from "@components/shared/ErrorBanner";
import { fetchCallFlows, saveCallFlow, deleteCallFlow } from "@services/callFlows";

const EMPTY_DRAFT = { flow_key: "", open_hours_text: "", after_hours_text: "", is_active: true };

const FLOW_KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;

function FlowRow({ flow, clinicId, onSaved, onDeleted, onError }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(flow);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(flow); }, [flow]);

  const dirty =
    (draft.open_hours_text ?? "") !== (flow.open_hours_text ?? "") ||
    (draft.after_hours_text ?? "") !== (flow.after_hours_text ?? "") ||
    draft.is_active !== flow.is_active;

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      await saveCallFlow(clinicId, flow.flow_key, {
        open_hours_text: draft.open_hours_text ?? "",
        after_hours_text: draft.after_hours_text ?? "",
        is_active: draft.is_active,
      });
      await onSaved();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteCallFlow(clinicId, flow.flow_key);
      await onDeleted();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          className="text-sm font-medium text-left flex-1"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "▾" : "▸"} {flow.flow_key}
          {!flow.is_active && <span className="ml-2 text-xs text-gray-500">(inactive)</span>}
        </button>
        <Button color="light" size="xs" disabled={busy} onClick={remove}>
          Delete
        </Button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={draft.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
            />
            <span>Active</span>
          </label>

          <Textarea
            label="During opening hours"
            rows={5}
            value={draft.open_hours_text ?? ""}
            onChange={(e) => set("open_hours_text", e.target.value)}
          />

          <Textarea
            label="After hours / weekends / holidays"
            rows={5}
            value={draft.after_hours_text ?? ""}
            onChange={(e) => set("after_hours_text", e.target.value)}
          />

          <div className="flex gap-2 justify-end">
            <Button color="light" size="sm" disabled={!dirty || busy} onClick={() => setDraft(flow)}>
              Reset
            </Button>
            <Button color="primary" size="sm" disabled={!dirty || busy} onClick={save}>
              {busy ? "Saving…" : "Save flow"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CallFlowsEditor({ clinicId }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newDraft, setNewDraft] = useState(EMPTY_DRAFT);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const res = await fetchCallFlows(clinicId);
      setFlows(res?.data?.call_flows ?? []);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  const canCreate =
    FLOW_KEY_RE.test(newDraft.flow_key) &&
    ((newDraft.open_hours_text ?? "").trim() || (newDraft.after_hours_text ?? "").trim()) &&
    !creating;

  const create = async () => {
    setCreating(true);
    try {
      await saveCallFlow(clinicId, newDraft.flow_key, {
        open_hours_text: newDraft.open_hours_text,
        after_hours_text: newDraft.after_hours_text,
        is_active: newDraft.is_active,
      });
      setNewDraft(EMPTY_DRAFT);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="text-sm font-semibold">Call Flows</div>
      <div className="text-xs text-gray-600">
        Named conversation flows (e.g. emergency). The backend sends the
        during-hours or after-hours wording depending on whether the clinic is
        open when the call comes in.
      </div>

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600 py-2">
          <Spinner size="sm" />
          <span>Loading call flows…</span>
        </div>
      ) : (
        <div className="space-y-2">
          {flows.length === 0 && (
            <div className="text-sm text-gray-500">No call flows configured.</div>
          )}
          {flows.map((flow) => (
            <FlowRow
              key={flow.flow_key}
              flow={flow}
              clinicId={clinicId}
              onSaved={load}
              onDeleted={load}
              onError={setError}
            />
          ))}
        </div>
      )}

      <div className="border-t pt-3 space-y-3">
        <div className="text-sm font-medium">Add flow</div>

        <Input
          label="Flow key"
          placeholder="e.g. emergency"
          value={newDraft.flow_key}
          onChange={(e) =>
            setNewDraft((d) => ({ ...d, flow_key: e.target.value.trim().toLowerCase() }))
          }
        />
        {newDraft.flow_key && !FLOW_KEY_RE.test(newDraft.flow_key) && (
          <div className="text-xs text-red-600">
            Use lowercase letters, digits and underscores, starting with a letter.
          </div>
        )}

        <Textarea
          label="During opening hours"
          rows={4}
          value={newDraft.open_hours_text}
          onChange={(e) => setNewDraft((d) => ({ ...d, open_hours_text: e.target.value }))}
        />

        <Textarea
          label="After hours / weekends / holidays"
          rows={4}
          value={newDraft.after_hours_text}
          onChange={(e) => setNewDraft((d) => ({ ...d, after_hours_text: e.target.value }))}
        />

        <div className="flex justify-end">
          <Button color="primary" size="sm" disabled={!canCreate} onClick={create}>
            {creating ? "Adding…" : "Add flow"}
          </Button>
        </div>
      </div>
    </div>
  );
}
